import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAction } from 'convex/react';
import { api } from './convex/_generated/api';
import { toast } from 'sonner';
import {
  ShieldCheck,
  Loader2,
  Mail,
} from 'lucide-react';

export default function MFAVerifyMeals() {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  const refs = useRef([]);

  const navigate = useNavigate();
  const location = useLocation();

  const userId = location.state?.userId;
  const email = location.state?.email;
  
  const verifyOTP = useAction(api.mfa.verifyOTP);
  const sendOTP = useAction(api.mfa.sendOTP);

  useEffect(() => {
    if (!userId) navigate('/');

    refs.current[0]?.focus();
  }, []);

  const handleChange = (val, idx) => {
    if (!/^\d?$/.test(val)) return;

    const next = [...digits];
    next[idx] = val;

    setDigits(next);

    if (val && idx < 5) {
      refs.current[idx + 1]?.focus();
    }

    if (val && idx === 5 && next.every((d) => d !== '')) {
      handleVerify(next.join(''));
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 6);

    if (paste.length === 6) {
      setDigits(paste.split(''));
      handleVerify(paste);
    }
  };

  const handleVerify = async (code) => {
    setVerifying(true);

    try {
      const result = await verifyOTP({ userId, code });

      if (result.verified) {
        localStorage.setItem('session_id', result.sessionId);

        toast.success('Verified! Welcome.');


        navigate('/pricingpage', {
          state: {
            sessionId: result.sessionId,
          },
        });
      }
    } catch (err) {
      toast.error(err.message || 'Invalid code');

      setDigits(['', '', '', '', '', '']);

      refs.current[0]?.focus();

      await sendOTP({ userId, email });
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setResending(true);

    try {
      await sendOTP({ userId, email });

      toast.success(`New code sent to ${email}`);
    } catch (err) {
      toast.error(err.message || 'Failed to resend');
    } finally {
      setResending(false);
    }
  };

  const token = digits.join('');

  return (
    <div style={styles.page}>
      <div style={styles.glow1} />
      <div style={styles.glow2} />

      <div style={styles.grid} />

      <div style={styles.card}>
        {/* TOP ICON */}
        <div style={styles.iconWrap}>
          <Mail size={34} color="#fff" />
        </div>

        {/* TITLE */}
        <h1 style={styles.title}>Verify Your Email</h1>

        <p style={styles.subtitle}>
          Enter the 6-digit verification code sent to
        </p>

        {/* EMAIL */}
        <div style={styles.emailBox}>
          <Mail size={16} color="#FF7A45" />
          <span>{email}</span>
        </div>

        {/* OTP INPUTS */}
        <div style={styles.digitsRow} onPaste={handlePaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (refs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => handleChange(e.target.value, i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              style={{
                ...styles.digitInput,
                borderColor: d
                  ? 'rgba(255,122,69,0.9)'
                  : '#F0E4D6',
                background: d
                  ? 'rgba(255,122,69,0.10)'
                  : '#FFF9F2',
                transform: d ? 'translateY(-2px)' : 'translateY(0)',
              }}
            />
          ))}
        </div>

        {/* VERIFY BUTTON */}
        <button
          onClick={() => token.length === 6 && handleVerify(token)}
          disabled={verifying || token.length < 6}
          style={{
            ...styles.btn,
            opacity: token.length < 6 ? 0.45 : 1,
            cursor: token.length < 6 ? 'not-allowed' : 'pointer',
          }}
        >
          {verifying ? (
            <Loader2 size={20} style={styles.spin} />
          ) : (
            'Verify & Continue'
          )}
        </button>

        {/* RESEND */}
        <button
          onClick={handleResend}
          disabled={resending}
          style={styles.resendBtn}
        >
          {resending
            ? 'Sending new code...'
            : "Didn't receive the code? Resend"}
        </button>

        {/* BACK */}
        <button
          onClick={() => navigate('/')}
          style={styles.backBtn}
        >
          Back to sign in
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    width: '100%',
    background:
      'linear-gradient(135deg, #FFF9F2 0%, #FFF3E6 45%, #FFEDDD 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    position: 'relative',
    overflow: 'hidden',
    fontFamily: "'Poppins', 'Inter', sans-serif",
  },

  grid: {
    position: 'absolute',
    inset: 0,
    backgroundImage:
      'linear-gradient(rgba(43,33,24,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(43,33,24,0.035) 1px, transparent 1px)',
    backgroundSize: '40px 40px',
    maskImage:
      'radial-gradient(circle at center, black 30%, transparent 90%)',
  },

  glow1: {
    position: 'absolute',
    top: -180,
    right: -120,
    width: 420,
    height: 420,
    background: 'rgba(255,122,69,0.20)',
    borderRadius: '50%',
    filter: 'blur(120px)',
    pointerEvents: 'none',
  },

  glow2: {
    position: 'absolute',
    bottom: -180,
    left: -120,
    width: 420,
    height: 420,
    background: 'rgba(255,180,67,0.18)',
    borderRadius: '50%',
    filter: 'blur(120px)',
    pointerEvents: 'none',
  },

  card: {
    width: '100%',
    maxWidth: 470,
    padding: '48px 40px',
    borderRadius: 32,
    background: '#FFFFFF',
    border: '1px solid #F0E4D6',
    position: 'relative',
    zIndex: 2,
    textAlign: 'center',
    boxShadow:
      '0 25px 70px rgba(43,33,24,0.10), inset 0 1px 0 rgba(255,255,255,0.6)',
    overflow: 'hidden',
  },

  iconWrap: {
    width: 84,
    height: 84,
    borderRadius: 28,
    background:
      'linear-gradient(135deg, #FF7A45 0%, #FFB443 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 28px',
    boxShadow: '0 20px 45px rgba(255,122,69,0.30)',
  },

  title: {
    fontSize: 36,
    fontWeight: 800,
    color: '#2B2118',
    margin: '0 0 14px',
    letterSpacing: '-0.04em',
    lineHeight: 1.1,
  },

  subtitle: {
    fontSize: 15,
    color: '#8A7A6C',
    lineHeight: 1.8,
    margin: '0 auto 18px',
    maxWidth: 340,
  },

  emailBox: {
    width: '100%',
    minHeight: 58,
    borderRadius: 18,
    background: '#FFF1E6',
    border: '1px solid #FFD9B8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    color: '#2B2118',
    fontSize: 15,
    fontWeight: 600,
    marginBottom: 34,
    padding: '0 18px',
    boxSizing: 'border-box',
    wordBreak: 'break-word',
  },

  digitsRow: {
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
    marginBottom: 34,
    flexWrap: 'wrap',
  },

  digitInput: {
    width: 56,
    height: 64,
    borderRadius: 18,
    border: '1px solid #F0E4D6',
    background: '#FFF9F2',
    fontSize: 24,
    fontWeight: 800,
    color: '#2B2118',
    textAlign: 'center',
    outline: 'none',
    transition: 'all 0.2s ease',
    fontFamily: "'Poppins', 'Inter', sans-serif",
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
  },

  btn: {
    width: '100%',
    height: 60,
    border: 'none',
    borderRadius: 18,
    background:
      'linear-gradient(135deg, #FF7A45 0%, #FFB443 100%)',
    color: '#fff',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    boxShadow: '0 18px 40px rgba(255,122,69,0.30)',
    marginBottom: 18,
  },

  resendBtn: {
    background: 'transparent',
    border: 'none',
    color: '#8A7A65',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    marginBottom: 12,
    transition: '0.2s ease',
  },

  backBtn: {
    background: 'transparent',
    border: 'none',
    color: '#A89684',
    fontSize: 13,
    cursor: 'pointer',
    transition: '0.2s ease',
  },

  spin: {
    animation: 'spin 1s linear infinite',
  },
};